import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Container,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
// import { Connection, PublicKey } from '@solana/web3.js';

import { colors, spacing, componentStyles } from '../styles/designSystem';

const useStyles = makeStyles((theme) => ({
  container: {
    ...componentStyles.sectionContainer,
    background: colors.gradients.hero,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(230, 0, 122, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
  },
  
  contentContainer: {
    ...componentStyles.contentContainer,
    position: 'relative',
    zIndex: 2,
  },
  card: {
    ...componentStyles.glassCard,
    height: '100%',
    padding: spacing.xl,
  },
  button: {
    ...componentStyles.button.primary,
    margin: spacing.sm,
  },
  alert: {
    margin: `${spacing.lg} 0`,
  },
  progress: {
    display: 'flex',
    justifyContent: 'center',
    margin: spacing.lg,
  },
}));

const SmartContractIntegration = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [presaleInfo, setPresaleInfo] = useState({
    totalRaised: 0,
    participants: 0,
    tokensSold: 0,
    remainingTokens: 0,
  });

  // Contract addresses (should be verified on Solana Explorer)
  // const PRESALE_CONTRACT = new PublicKey('YOUR_VERIFIED_PRESALE_CONTRACT_ADDRESS');

  // const connection = new Connection(process.env.REACT_APP_SOLANA_RPC_URL || 'https://api.mainnet-beta.solana.com');

  // TODO: Replace with actual contract integration when contracts are deployed
  // This is placeholder code for development purposes
  const fetchPresaleInfo = useCallback(async () => {
    try {
      // TODO: Replace with actual contract calls when contracts are deployed
      // For now, using mock data to demonstrate UI functionality
      console.log('Fetching presale info... (placeholder)');
      
      // Mock data for development
      setPresaleInfo({
        totalRaised: 150, // SOL raised
        participants: 45,
        tokensSold: 150000000, // LLAMA tokens sold
        remainingTokens: 850000000,
      });
      
      // TODO: Uncomment when contracts are deployed
      // const presaleAccount = await connection.getAccountInfo(PRESALE_CONTRACT);
      // if (presaleAccount) {
      //   // Parse contract data (implementation depends on your contract structure)
      //   setPresaleInfo({
      //     totalRaised: 150,
      //     participants: 45,
      //     tokensSold: 150000000,
      //     remainingTokens: 850000000,
      //   });
      // }
    } catch (error) {
      console.error('Error fetching presale info:', error);
    }
  }, []); // Empty dependency array for now - will add actual dependencies when contracts are deployed

  useEffect(() => {
    fetchPresaleInfo();
  }, [fetchPresaleInfo]);

  // TODO: Replace with actual contract integration when contracts are deployed
  // This is placeholder code for development purposes
  const participateInPresale = async (amount) => {
    if (!window.solana || !window.solana.isPhantom) {
      setError('Please install Phantom wallet');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // TODO: Replace with actual contract calls when contracts are deployed
      // For now, using mock functionality to demonstrate UI
      console.log('Participating in presale... (placeholder)');
      
      // Mock wallet connection for development
      // const response = await window.solana.connect();
      // const publicKey = response.publicKey;
      
      // Mock transaction for development
      setSuccess(`Mock participation successful! Amount: ${amount} SOL`);
      
      // TODO: Uncomment when contracts are deployed
      // const transaction = new Transaction();
      // transaction.add(
      //   SystemProgram.transfer({
      //     fromPubkey: publicKey,
      //     toPubkey: PRESALE_CONTRACT,
      //     lamports: amount * LAMPORTS_PER_SOL,
      //   })
      // );
      // const { blockhash } = await connection.getLatestBlockhash();
      // transaction.recentBlockhash = blockhash;
      // transaction.feePayer = publicKey;
      // const signed = await window.solana.signTransaction(transaction);
      // const signature = await connection.sendRawTransaction(signed.serialize());
      // await connection.confirmTransaction(signature);
      // setSuccess(`Successfully participated in presale! Transaction: ${signature}`);
      
      fetchPresaleInfo(); // Refresh presale info
      
    } catch (error) {
      setError(`Transaction failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };



  return (
    <Box className={classes.container}>
      <Container className={classes.contentContainer}>
        <Typography variant="h4" gutterBottom color="primary">
          Secure Presale Participation
        </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Presale Statistics
              </Typography>
              <Typography variant="body1">
                Total Raised: {presaleInfo.totalRaised} SOL
              </Typography>
              <Typography variant="body1">
                Participants: {presaleInfo.participants}
              </Typography>
              <Typography variant="body1">
                Tokens Sold: {presaleInfo.tokensSold.toLocaleString()} LLAMA
              </Typography>
              <Typography variant="body1">
                Remaining: {presaleInfo.remainingTokens.toLocaleString()} LLAMA
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Participate in Presale
              </Typography>
              <Typography variant="body2" paragraph>
                Send SOL to participate in the presale. Tokens will be automatically distributed
                after the presale ends and liquidity is locked.
              </Typography>
              
              <Box>
                <Button
                  className={classes.button}
                  onClick={() => participateInPresale(1)}
                  disabled={loading}
                  fullWidth
                  variant="contained"
                >
                  Participate with 1 SOL
                </Button>
                
                <Button
                  className={classes.button}
                  onClick={() => participateInPresale(5)}
                  disabled={loading}
                  fullWidth
                  variant="contained"
                >
                  Participate with 5 SOL
                </Button>
                
                <Button
                  className={classes.button}
                  onClick={() => participateInPresale(10)}
                  disabled={loading}
                  fullWidth
                  variant="contained"
                >
                  Participate with 10 SOL
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading && (
        <Box className={classes.progress}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" className={classes.alert}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" className={classes.alert}>
          {success}
        </Alert>
      )}

      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          Security Features
        </Typography>
        <Typography variant="body2" paragraph>
          • Smart contract verified on Solana Explorer
        </Typography>
        <Typography variant="body2" paragraph>
          • Liquidity will be locked for 1 year
        </Typography>
        <Typography variant="body2" paragraph>
          • Team tokens locked for 6 months
        </Typography>
        <Typography variant="body2" paragraph>
          • All transactions are transparent and verifiable
        </Typography>
      </Box>
      </Container>
    </Box>
  );
};

export default SmartContractIntegration; 